import React, { useState } from "react";
import { toast } from "sonner";
import { DIRECTION_LABEL, useLang } from "@/i18n";
import { api, apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const DEFAULT_BHX = "100 Warward Lane, Selly Oak, B29 7RD, Birmingham, UK";
const DEFAULT_GRESIK = "HSB, Jl. Taman Perintis 7 No. 19, Randuagung, Kebomas, Gresik, East Java 61121";

const blank = {
  direction: "bhx_gresik",
  title: "",
  status: "open",
  dropoff_address: DEFAULT_BHX,
  pickup_address: DEFAULT_GRESIK,
  receive_deadline: "",
  deadline_timezone: "birmingham",
  capacity_kg: 50,
  standard_fee_gbp_per_kg: 8,
  bulky_pricing_type: "flat",
  bulky_fee_gbp: 25,
  exchange_rate_gbp_to_idr: 20500,
  active: true,
  order_open: true,
};

const toLocalInput = (iso) => (iso ? iso.slice(0, 16) : "");

export const TripDialog = ({ open, onOpenChange, trip, onSaved }) => {
  const editing = !!trip;
  const [form, setForm] = useState(blank);

  React.useEffect(() => {
    if (open) {
      setForm(trip ? { ...blank, ...trip, receive_deadline: toLocalInput(trip.receive_deadline) } : blank);
    }
  }, [open, trip]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!form.title.trim() || !form.receive_deadline) { toast.error("Title and receive deadline are required"); return; }
    const payload = {
      ...form,
      capacity_kg: Number(form.capacity_kg),
      standard_fee_gbp_per_kg: Number(form.standard_fee_gbp_per_kg),
      bulky_fee_gbp: Number(form.bulky_fee_gbp),
      exchange_rate_gbp_to_idr: Number(form.exchange_rate_gbp_to_idr),
      receive_deadline: form.receive_deadline.length === 16 ? form.receive_deadline + ":00" : form.receive_deadline,
    };
    try {
      if (editing) await api.put(`/admin/trips/${trip.id}`, payload);
      else await api.post("/admin/trips", payload);
      toast.success(editing ? "Trip updated" : "Trip created");
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="trip-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">{editing ? "Edit Trip" : "Create Trip"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <F label="Direction">
              <Select value={form.direction} onValueChange={(v) => set("direction", v)}>
                <SelectTrigger data-testid="trip-direction"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bhx_gresik">{DIRECTION_LABEL.bhx_gresik}</SelectItem>
                  <SelectItem value="gresik_bhx">{DIRECTION_LABEL.gresik_bhx}</SelectItem>
                </SelectContent>
              </Select>
            </F>
            <F label="Status">
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="trip-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["open", "closed", "in_transit", "arrived", "completed"].map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </F>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <F label="Homepage visibility">
              <Select value={form.active ? "true" : "false"} onValueChange={(v) => set("active", v === "true")}>
                <SelectTrigger data-testid="trip-active"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">Active (visible on site)</SelectItem>
                  <SelectItem value="false">Inactive (hidden from site)</SelectItem>
                </SelectContent>
              </Select>
            </F>
            <F label="Accepting bookings">
              <Select value={form.order_open ? "true" : "false"} onValueChange={(v) => set("order_open", v === "true")}>
                <SelectTrigger data-testid="trip-orderopen"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">Orders open</SelectItem>
                  <SelectItem value="false">Orders closed (still visible)</SelectItem>
                </SelectContent>
              </Select>
            </F>
          </div>
          <F label="Trip title">
            <Input data-testid="trip-title" value={form.title} onChange={(e) => set("title", e.target.value)}
              placeholder="Open Jastip Birmingham–Gresik | 18 September 2026" />
          </F>
          <div className="grid sm:grid-cols-2 gap-4">
            <F label="Drop-off address"><Textarea data-testid="trip-dropoff" value={form.dropoff_address} onChange={(e) => set("dropoff_address", e.target.value)} /></F>
            <F label="Pick-up address"><Textarea data-testid="trip-pickup" value={form.pickup_address} onChange={(e) => set("pickup_address", e.target.value)} /></F>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <F label="Latest date/time to receive goods">
              <Input data-testid="trip-deadline" type="datetime-local" value={form.receive_deadline} onChange={(e) => set("receive_deadline", e.target.value)} />
            </F>
            <F label="Deadline timezone">
              <Select value={form.deadline_timezone} onValueChange={(v) => set("deadline_timezone", v)}>
                <SelectTrigger data-testid="trip-tz"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="birmingham">Birmingham time (BHX)</SelectItem>
                  <SelectItem value="gresik">Gresik time (WIB)</SelectItem>
                </SelectContent>
              </Select>
            </F>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <F label="Baggage capacity (kg)"><Input data-testid="trip-capacity" type="number" value={form.capacity_kg} onChange={(e) => set("capacity_kg", e.target.value)} /></F>
            <F label="Standard fee per kg (GBP)"><Input data-testid="trip-fee" type="number" step="0.01" value={form.standard_fee_gbp_per_kg} onChange={(e) => set("standard_fee_gbp_per_kg", e.target.value)} /></F>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <F label="Bulky pricing">
              <Select value={form.bulky_pricing_type} onValueChange={(v) => set("bulky_pricing_type", v)}>
                <SelectTrigger data-testid="trip-bulky-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="flat">Flat fee</SelectItem>
                  <SelectItem value="per_kg">Per kg</SelectItem>
                  <SelectItem value="quote">Manual quote</SelectItem>
                </SelectContent>
              </Select>
            </F>
            <F label="Bulky fee (GBP)"><Input data-testid="trip-bulky-fee" type="number" step="0.01" value={form.bulky_fee_gbp} onChange={(e) => set("bulky_fee_gbp", e.target.value)} disabled={form.bulky_pricing_type === "quote"} /></F>
            <F label="Exchange rate £1 → IDR"><Input data-testid="trip-rate" type="number" value={form.exchange_rate_gbp_to_idr} onChange={(e) => set("exchange_rate_gbp_to_idr", e.target.value)} /></F>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="trip-save-btn" onClick={save}>Save Trip</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const F = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-sm">{label}</Label>{children}</div>
);
